// @flow
import * as React from 'react';
import {useEffect, useState, useRef} from 'react';
import {Helmet} from 'react-helmet-async';
import Box from 'sail/Box';
import {usePrefs} from 'docs/data';
import {useFile, useStep, useCondition} from 'docs/data/integration-builder';
import {load, getActiveFile} from 'docs/util';
import type {LoadResult} from 'docs/util';
import type {SettingType} from 'docs/data/integration-builder';
import {Heading, Paragraph, NextSteps} from 'docs/components';
import {useContentFrame} from 'docs/shell/content/ContentFrameProvider';
import NarrowScreenWarning from 'docs/interactive/IntegrationBuilder/components/NarrowScreenWarning';
import {IntegrationBuilderButtonGroup} from './IntegrationBuilderButtonGroup';
import RightContainer from './RightContainer';
import LeftContainer from './LeftContainer';
import {Settings, getAvailableSettings} from './Settings/Settings';

import './IntegrationBuilder.scss';
import {Feedback} from './Feedback';

export type IntegrationBuilderType = 'webhooks' | 'checkout' | 'payments';

const findStep = (list) => {
  const ACTIVATION_THRESHOLD = 120;
  const item = list.find((el) => {
    return el.getBoundingClientRect().top > ACTIVATION_THRESHOLD;
  });

  return item;
};

const findIndex = (list, id) => {
  return list.findIndex((el) => el.dataset.id === id);
};

type IntegrationBuilderProps = {|
  settings: SettingType,
  type: IntegrationBuilderType,
  title: React.Element<typeof Heading>,
  description: Array<React.Element<typeof Paragraph>>,
  demo: React.Element<any>,
  nextSteps: React.Element<typeof NextSteps>,
  textGuideHref: string,
  children: Array<React.Element<any>>,
|};

export const IntegrationBuilder = ({
  settings = 'default',
  type,
  title,
  description,
  demo,
  nextSteps,
  textGuideHref,
  children,
}: IntegrationBuilderProps) => {
  const {getPref} = usePrefs();
  const [previousScrollPosition, setPreviousScrollPosition] = useState(0);
  const {components, setComponents, setActiveFile, activeFile} = useFile();
  const {activeStep, setActiveStep} = useStep();
  const {toggles} = useCondition();
  const {onScroll, getScrollPosition} = useContentFrame();
  const setPreviousScrollPositionRef = useRef(setPreviousScrollPosition);

  useEffect(() => {
    const removeEvent = onScroll(() => {
      // TODO(adreyfus): Track elements via refs rather than querying the DOM
      const list = Array.from(document.getElementsByClassName('Step'));
      const el = findStep(list);

      if (!el || el.dataset.id === activeStep) {
        // Change the active step only if we have scrolled to a new step
        return;
      }

      // Track whether we are scrolling up or down
      const scrollUp = getScrollPosition() < previousScrollPosition;
      setPreviousScrollPositionRef.current(getScrollPosition());

      const activeStepIndex = findIndex(list, activeStep);
      const elIndex = findIndex(list, el.dataset.id);

      if (
        (scrollUp && activeStepIndex < elIndex) ||
        (!scrollUp && activeStepIndex > elIndex)
      ) {
        // Skip activating the step unless it's directly next in line
        // based on the direction we are scrolling
        return;
      }

      // Switch the File if the Step is in a different File
      const fileWithStep = getActiveFile(components, el.dataset.id);
      if (fileWithStep && fileWithStep !== activeFile) {
        setActiveFile(fileWithStep);
      }

      setActiveStep(el.dataset.id);
    });
    return removeEvent;
  }, [
    activeStep,
    components,
    activeFile,
    getScrollPosition,
    onScroll,
    setActiveStep,
    setActiveFile,
    previousScrollPosition,
  ]);

  const {files, steps}: LoadResult = React.useMemo(() => {
    const conditions = {
      platform: getPref('platform'),
      client: getPref('client'),
      server: getPref('server') || getPref('lang'),
      reader: getPref('reader'),
      toggles,
    };
    return load({
      type: settings,
      conditions,
      settings: getAvailableSettings(settings),
      children,
    });
  }, [settings, children, getPref, toggles]);

  setComponents(files);

  if (activeFile === '') {
    setActiveFile(Object.keys(files)[0]);
  }

  return (
    <Box
      id="integration-builder"
      className="IntegrationBuilder"
      background="gray50"
      flex={{direction: 'column'}}
      flexGrow={1}
    >
      <Helmet htmlAttributes={{style: 'width: 100vw'}}>
        {/* Make sure the IntegrationBuilder does not render zoomed in on small screen devices */}
        <meta name="viewport" content="" />
      </Helmet>
      <NarrowScreenWarning />
      <Settings type={settings} />
      <Box flex={{direction: 'row'}}>
        <Box flex={{direction: 'column'}} className="Container">
          <Box flex>
            {steps && (
              <LeftContainer
                title={title}
                description={description}
                textGuideHref={textGuideHref}
                buttonGroup={<IntegrationBuilderButtonGroup type={type} />}
              >
                {steps}
                {nextSteps}
                <Feedback />
              </LeftContainer>
            )}
            <RightContainer demo={demo} />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
